import { Flex } from "@redotech/arbiter/flex";
import { RedoInputSize } from "@redotech/arbiter/input/base-redo-text-input";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { RedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoListDropdown } from "@redotech/arbiter/list/redo-list-dropdown";
import { useInputAutocomplete } from "@redotech/react-util/autocomplete";
import { genericMemo } from "@redotech/react-util/component";
import { useState } from "react";

export const AutoCompleteInput = genericMemo(function AutoCompleteInput<T, K>({
  getSuggestions,
  pickSuggestion,
  setAutocompleteFields,
  setValue,
  value,
  label,
  placeholder,
  dataTestId,
  size = "sm",
}: {
  getSuggestions: (
    query: string,
  ) => Promise<{ keyToFullObject: K; displayText: string }[]>;
  pickSuggestion: (keyToFullObject: K) => Promise<T | undefined | null>;
  setAutocompleteFields: (fields: T) => void;
  setValue: (value: string) => void;
  value: string;
  label: string;
  placeholder: string;
  dataTestId?: string;
  size?: RedoInputSize;
}) {
  const [autocompleteDropdownAnchor, setAutocompleteDropdownAnchor] =
    useState<HTMLElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const { setQuery, suggestionsLoad, setKeyToFullObject } =
    useInputAutocomplete<T, K>({
      getSuggestions,
      pickSuggestion,
      setAutocompleteFields,
    });

  return (
    <Flex dir="column" gap="none">
      <Flex dir="column" ref={setAutocompleteDropdownAnchor} w="full">
        <RedoTextInput
          dataTestId={dataTestId}
          label={label}
          onFocus={() => {
            setDropdownOpen(true);
          }}
          placeholder={placeholder}
          setValue={(value) => {
            setDropdownOpen(true);
            setValue(value);
            setQuery(value);
          }}
          size={size}
          value={value}
        />
      </Flex>

      <RedoListDropdown
        dropdownAnchor={autocompleteDropdownAnchor}
        dropdownOpen={dropdownOpen}
        emptyListMessage={
          value.length > 0 ? "No results found" : "Type to search"
        }
        fitToAnchor
        items={
          suggestionsLoad.value?.map(
            (suggestion, index): RedoListItem<K> => ({
              id: `${suggestion.displayText}-${index}`,
              type: "text",
              text: suggestion.displayText,
              value: suggestion.keyToFullObject,
            }),
          ) ?? []
        }
        itemSelected={({ item }) => {
          setKeyToFullObject(item.value);
        }}
        preventCloseOnAnchorClick
        setDropdownOpen={setDropdownOpen}
        size="xs"
      />
    </Flex>
  );
});
